import { FormEvent, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import type { KnowledgeGapView, SourceHealth } from "@shared/types";
import { StatusPill } from "../components/Status";

const TABS = ["overview", "sources", "gaps", "validation", "account"] as const;

export function Workspace() {
  const { tab } = useParams();
  const active = (TABS as readonly string[]).includes(tab ?? "") ? tab! : "overview";
  const [me, setMe] = useState<{ user: { email: string; isAdmin: boolean } | null }>({ user: null });
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [sources, setSources] = useState<SourceHealth[]>([]);
  const [gaps, setGaps] = useState<KnowledgeGapView[]>([]);
  const [queue, setQueue] = useState<{ id: string; object_id: string; object_type: string; reason: string; status: string }[]>([]);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api<{ user: { email: string; isAdmin: boolean } | null }>("/api/auth/me").then(setMe).catch(() => setMe({ user: null }));
    api<{ sources: SourceHealth[] }>("/api/sources").then((r) => setSources(r.sources)).catch(() => setSources([]));
    api<{ gaps: KnowledgeGapView[] }>("/api/knowledge-gaps").then((r) => setGaps(r.gaps)).catch(() => setGaps([]));
    api<{ queue: typeof queue }>("/api/workspace/validation-queue").then((r) => setQueue(r.queue)).catch(() => setQueue([]));
  }, []);

  async function requestLink(e: FormEvent) {
    e.preventDefault();
    const r = await api<{ message: string; token?: string }>("/api/auth/request", { method: "POST", body: JSON.stringify({ email }) });
    setMsg(r.message);
    if (r.token) setToken(r.token);
  }
  async function complete(e: FormEvent) {
    e.preventDefault();
    await api("/api/auth/complete", { method: "POST", body: JSON.stringify({ token }) });
    const me2 = await api<{ user: { email: string; isAdmin: boolean } | null }>("/api/auth/me");
    setMe(me2);
  }

  return (
    <section className="page-hero">
      <div className="wrap">
        <p className="kicker">Institutional workspace</p>
        <h1>Private knowledge stays private.</h1>
        <p className="lede">Public search and Ask Floodplains never see organisation or confidential objects.</p>
        <div className="tabs">
          {TABS.map((t) => (
            <Link key={t} to={`/workspace/${t}`}>
              <button className={active === t ? "active" : ""} type="button">
                {t}
              </button>
            </Link>
          ))}
        </div>
        <div className="panel">
          {active === "overview" && (
            <div className="grid-2">
              <article className="card">
                <h3>Handover</h3>
                <p className="muted">Export a Model Continuity Pack for consultant changeover, staff departure, or audit.</p>
                <p><a className="btn" href="/api/continuity/ross-river">Ross River pack (JSON)</a></p>
              </article>
              <article className="card">
                <h3>Permissions</h3>
                <p className="muted">public · organisation · project · confidential/private. Private ingestion is scaffolded; it cannot leak into public AI responses.</p>
              </article>
            </div>
          )}
          {active === "sources" && (
            <table className="table">
              <thead>
                <tr><th>Source</th><th>Status</th><th>Last success</th><th>Consequence</th></tr>
              </thead>
              <tbody>
                {sources.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}<br /><span className="muted small">{s.rootDomain}</span></td>
                    <td><StatusPill status={s.status} /></td>
                    <td className="font-data">{s.lastSuccessAt ?? "—"}</td>
                    <td className="muted small">{s.consequence}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {active === "gaps" &&
            gaps.map((g) => (
              <article className="card" key={g.id} id={g.id} style={{ marginBottom: 12 }}>
                <h3>{g.question}</h3>
                <p className="muted small">{g.status} · {g.modelName}</p>
                {g.response && <p>{g.response.text}</p>}
              </article>
            ))}
          {active === "validation" && (
            <table className="table">
              <thead>
                <tr><th>Object</th><th>Type</th><th>Reason</th><th>Status</th></tr>
              </thead>
              <tbody>
                {queue.map((q) => (
                  <tr key={q.id}>
                    <td className="font-data">{q.object_id}</td>
                    <td>{q.object_type}</td>
                    <td>{q.reason}</td>
                    <td>{q.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {active === "account" && (
            <article className="card" style={{ maxWidth: 520 }}>
              {me.user ? (
                <p>Signed in as {me.user.email}</p>
              ) : (
                <>
                  <form onSubmit={requestLink}>
                    <p>Magic link (local token returned in development).</p>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@council.gov.au" />
                    <button className="btn" type="submit" style={{ marginTop: 8 }}>Request link</button>
                  </form>
                  {token && (
                    <form onSubmit={complete} style={{ marginTop: 16 }}>
                      <input value={token} onChange={(e) => setToken(e.target.value)} />
                      <button className="btn" type="submit" style={{ marginTop: 8 }}>Complete sign-in</button>
                    </form>
                  )}
                  {msg && <p className="muted small">{msg}</p>}
                </>
              )}
            </article>
          )}
        </div>
      </div>
    </section>
  );
}
